import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Pin, Lock, Eye, MessageSquare, ChevronLeft, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Spinner, UserChip, Time, Empty, Avatar } from "@/forum/ui";

export const ThreadRow = ({ t, showCategory }) => (
  <Link
    to={`/forum/t/${t.id}`}
    data-testid={`thread-${t.id}`}
    className="group grid grid-cols-[auto_1fr] items-center gap-4 px-5 py-4 transition-colors hover:bg-white/[0.03] sm:grid-cols-[auto_1fr_auto_auto] sm:px-6"
  >
    <Avatar user={t.author} />
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        {t.pinned && <Pin size={12} className="text-[var(--glow)]" data-testid="pinned-icon" />}
        {t.locked && <Lock size={12} className="text-white/40" data-testid="locked-icon" />}
        <h3 className="truncate font-display text-[15px] font-semibold text-white group-hover:text-[var(--glow)] sm:text-base">{t.title}</h3>
      </div>
      <p className="mt-1 font-mono text-[11px] text-white/40">
        {t.author?.username} · <Time iso={t.created_at} />{showCategory && t.category_name ? ` · ${t.category_name}` : ""}
      </p>
    </div>
    <div className="hidden items-center gap-4 font-mono text-[11px] text-white/40 sm:flex">
      <span className="inline-flex items-center gap-1"><MessageSquare size={12} />{t.reply_count}</span>
      <span className="inline-flex items-center gap-1"><Eye size={12} />{t.views}</span>
    </div>
    <div className="hidden w-40 text-right sm:block">
      <p className="text-xs text-white/70">{t.last_post_by?.username}</p>
      <Time iso={t.last_post_at} />
    </div>
  </Link>
);

export const Pager = ({ page, pages, onChange }) => pages > 1 && (
  <div className="flex items-center justify-center gap-2 font-mono text-xs" data-testid="pager">
    {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
      <button key={p} type="button" onClick={() => onChange(p)} className={`h-8 min-w-8 rounded-full px-2 ${p === page ? "bg-white text-black" : "border border-white/10 text-white/60 hover:text-white"}`} data-testid={`page-${p}`}>{p}</button>
    ))}
  </div>
);

export default function CategoryPage() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const page = Number(sp.get("page") || 1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    if (user === null) return;
    setData(null);
    api.get(`/forum/categories/${slug}/threads`, { params: { page } }).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || "Hiba"));
  }, [slug, page, user]);

  if (err) return <Empty text={err} testId="category-error" />;
  if (!data) return <Spinner />;
  const { category: c, threads } = data;

  return (
    <div className="space-y-6" data-testid="category-page">
      <Link to="/forum" className="inline-flex items-center gap-1 font-mono text-[11px] tracking-widest text-white/50 hover:text-white" data-testid="back-to-forum"><ChevronLeft size={12} />FÓRUM</Link>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="eyebrow mb-3">{c.group}</p>
          <h1 className="font-display text-3xl font-bold sm:text-4xl" data-testid="category-title">{c.name}</h1>
          <p className="mt-2 max-w-xl text-sm text-white/50">{c.description}</p>
        </div>
        {c.can_write ? (
          <Link to={`/forum/new/${c.slug}`} className="btn-glow inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black" data-testid="new-thread-button"><Plus size={16} />Új téma</Link>
        ) : user ? (
          <span className="font-mono text-[11px] text-white/40" data-testid="no-write-permission">Ide csak {c.min_write_role} rangtól lehet írni</span>
        ) : (
          <Link to="/login" className="rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/5" data-testid="login-to-post">Belépés a hozzászóláshoz</Link>
        )}
      </div>
      <Panel className="overflow-hidden">
        {threads.length === 0 ? <Empty text="Még nincs téma ebben a kategóriában. Legyél te az első!" testId="no-threads" /> : (
          <div className="divide-y divide-white/[0.05]">{threads.map((t) => <ThreadRow key={t.id} t={t} />)}</div>
        )}
      </Panel>
      <Pager page={data.page} pages={data.pages} onChange={(p) => setSp({ page: p })} />
    </div>
  );
}
